// src/pages/Detection.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { FaChartBar } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card-md px-4 py-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

function Detection() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("auth")) window.location.href = "/login";
  }, []);

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/detections/stats`);
        setStats(res.data || []);
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">Loading detections…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-xl space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Detection Results</h1>
        <p className="text-sm text-slate-500 mt-0.5">Aggregated statistics per day</p>
      </div>

      {/* Chart */}
      {stats.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <FaChartBar className="text-blue-500" />
            <span className="section-title">Daily Detection Overview</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#64748b", paddingTop: 12 }} iconType="circle" iconSize={8} />
              <Bar dataKey="person_total"    fill="#3b82f6" name="Persons"   radius={[4, 4, 0, 0]} />
              <Bar dataKey="machinery_total" fill="#10b981" name="Machinery" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      {stats.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <FaChartBar className="text-slate-300 text-lg" />
          </div>
          <div className="text-sm text-slate-500">No detection data found.</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="section-title">Detection Records</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Persons</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Machinery</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Files</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {stats.map((row, idx) => (
                  <tr key={row.date}
                    className="hover:bg-slate-50 transition-colors duration-100">
                    <td className="px-5 py-3 font-medium text-slate-800">{row.date}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                        text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                        {row.person_total}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                        text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {row.machinery_total}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500">{row.file_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Detection;
